/**
 * HTTP API contract shared by the Worker, the web/native client and the
 * admin console. Every route is under /v1 (public) or /admin (Cloudflare
 * Access-protected). Binary bodies are documented inline.
 */

import type { BadgeCategory } from './badges.ts';
import type { ParameterKey } from './params.ts';

export interface ApiError {
  error: string;
  message?: string;
  details?: unknown;
}

// ---------------------------------------------------------------------------
// Drawing (spec §4.4)
// ---------------------------------------------------------------------------

export interface DrawOp {
  /** Row-major index within the tile: y * 256 + x. */
  idx: number;
  /** Palette index 1–255. */
  colour: number;
}

export interface DrawRequest {
  tile: string;
  lat: number;
  lng: number;
  /** GPS accuracy in metres (68 % radius). */
  accuracy: number;
  /** OS-level mock-location flag; any truthy value is rejected. */
  mockFlag?: boolean;
  ops: DrawOp[];
  /** Client UTC offset in minutes (for local-time badges only). */
  tzOffsetMinutes?: number;
  /** Set by agent callers; user tokens may not set it. */
  source?: 'user' | 'agent';
}

export interface DrawResponse {
  applied: number;
  /** Ops that were rejected (already the same colour, out of range, …). */
  skipped: number;
  ink: InkState;
  /** Badges earned synchronously (badges evaluated on the queue arrive by notification). */
  seq: number;
}

export interface InkState {
  ink: number;
  cap: number;
  accrualSeconds: number;
  /** Unix-ms when the next unit of ink accrues (null when at cap or frozen). */
  nextAt: number | null;
  frozenUntil?: number | null;
}

export interface UndoRequest {
  tile: string;
  idx: number;
}

// ---------------------------------------------------------------------------
// Users / identity (spec §2.6, §2.7)
// ---------------------------------------------------------------------------

export type UserStatus = 'active' | 'warned' | 'frozen' | 'suspended' | 'banned' | 'tombstone';

export interface NotificationPrefs {
  push: boolean;
  inApp: boolean;
  overwrite: boolean;
  collab: boolean;
  badge: boolean;
  moderation: boolean;
}

export const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = {
  push: true,
  inApp: true,
  overwrite: true,
  collab: true,
  badge: true,
  moderation: true,
};

export interface Me {
  id: number;
  handle: string;
  /** 256 palette indices (16 × 16), base64. */
  avatar: string | null;
  createdAt: number;
  status: UserStatus;
  penaltyUntil: number | null;
  strikes: number;
  ink: InkState;
  stats: PublicStats;
  badges: EarnedBadge[];
  notificationPrefs: NotificationPrefs;
  introSeen: boolean;
  isAgent: boolean;
  /** Unix-ms until which this user is "live" (avatar + handle shown where they paint), or null. */
  liveUntil: number | null;
}

/** A painter who opted in to be shown live on the map (spec §2.4 avatars are opt-in only). */
export interface LivePainter {
  userId: number;
  handle: string;
  /** 256 palette indices, base64, or null. */
  avatar: string | null;
  /** Pixel index of their most recent paint in this tile. */
  idx: number;
  ts: number;
}

/** GET /v1/live — live painters across the planet at level-13 cell resolution (globe callouts). */
export interface GlobalLivePainter {
  userId: number;
  handle: string;
  avatar: string | null;
  cell: string;
  ts: number;
}

/** A painted tile inside an activity cell (public aggregate, no person attached). */
export interface CellTile {
  tile: string;
  paintedCount: number;
  at: number;
}

/** Tile WebSocket INFO payload for presence updates. */
export interface PresenceInfo {
  type: 'presence';
  tile: string;
  painters: LivePainter[];
}

export interface PublicStats {
  pixelsPainted: number;
  tilesVisited: number;
}

export interface EarnedBadge {
  key: string;
  name: string;
  description: string;
  category: BadgeCategory;
  earnedAt: number;
}

export interface SignUpRequest {
  /** 13+ self-attestation (spec §2.7). Must be true. */
  ageAttested: boolean;
}

export interface AvatarRequest {
  /** 256 palette indices, base64 (0 = empty). */
  avatar: string;
}

export interface AvatarResponse {
  avatar: string;
  cost: number;
  ink: InkState;
  completed: boolean;
}

export interface PushTokenRequest {
  platform: 'ios' | 'android' | 'web';
  token: string;
}

export type MetaEventRequest = { type: 'playback' } | { type: 'globe_view'; cells: string[] } | { type: 'intro_seen' };

export interface NotificationItem {
  id: number;
  kind: 'overwrite' | 'collab' | 'badge' | 'moderation';
  tile: string | null;
  title: string;
  body: string;
  /** Deep link, e.g. /t/{tileId}. Never contains coordinates. */
  link: string | null;
  createdAt: number;
  readAt: number | null;
}

// ---------------------------------------------------------------------------
// Tiles, playback, activity (spec §2.3, §4.5, §4.6)
// ---------------------------------------------------------------------------

/**
 * GET /v1/tiles/{tile}            → 65536 raw palette bytes (gzip-encoded on the wire)
 * GET /v1/tiles/{tile}.png        → RGBA render
 * GET /v1/tiles/{tile}/playback?date=YYYY-MM-DD[&excludeAgents=1] → 65536 bytes
 * GET /v1/tiles/{tile}/dates      → PlaybackDates
 * GET /v1/tiles/{tile}/ws         → WebSocket (see protocol.ts)
 */
export interface PlaybackDates {
  tile: string;
  /** Days (UTC) that have a snapshot, ascending. */
  dates: string[];
  firstDate: string | null;
}

export interface TileInfo {
  tile: string;
  cell: string;
  paintedCount: number;
  firstAt: number | null;
  lastEventAt: number | null;
  subscribers: number;
}

/** GET /v1/stats — public, cached briefly. */
export interface SiteStats {
  painters: number;
  places: number;
  pixels: number;
  liveCells: number;
  recent: Array<{ tile: string; at: number; paintedCount: number }>;
}

export interface ActivityCell {
  cell: string;
  count: number;
  ts: number;
}

export interface ActivityBatch {
  type: 'activity';
  ts: number;
  cells: ActivityCell[];
}

/** GET /v1/globe/cells → binary: repeated { cellToken:16 bytes utf8 (space padded), r,g,b, count u32 } — see protocol.ts */
export interface GlobeCellSummary {
  cell: string;
  r: number;
  g: number;
  b: number;
  count: number;
}

// ---------------------------------------------------------------------------
// Reports & moderation (spec §2.10)
// ---------------------------------------------------------------------------

export type ReportReason = 'hate' | 'harassment' | 'sexual' | 'personal_info' | 'other';
export const REPORT_REASONS: ReportReason[] = ['hate', 'harassment', 'sexual', 'personal_info', 'other'];

/** Rectangular region in a tile's local (i, j) pixel coordinates, inclusive. */
export interface TileRegion {
  i0: number;
  j0: number;
  i1: number;
  j1: number;
}

export interface ReportRequest {
  tile: string;
  region: TileRegion;
  reason: ReportReason;
  note?: string;
}

export type ReportStatus = 'open' | 'actioned' | 'dismissed';

export interface Report {
  id: number;
  tile: string;
  region: TileRegion;
  reason: ReportReason;
  note: string | null;
  reporterId: number | null;
  status: ReportStatus;
  weight: number;
  createdAt: number;
  resolvedAt: number | null;
  source: 'user' | 'spoof' | 'classifier';
}

export type ModActionKind =
  | 'undo_user'
  | 'undo_region'
  | 'warn'
  | 'ink_freeze'
  | 'suspend'
  | 'ban'
  | 'ink_penalty'
  | 'strike'
  | 'dismiss'
  | 'unban';

export interface ModActionRequest {
  action: ModActionKind;
  targetUserId?: number;
  /** For undo_region: snapshot date to revert to (YYYY-MM-DD); omit for "before the report". */
  snapshotDate?: string;
  /** For ink_penalty. */
  amount?: number;
  note?: string;
}

export interface ModAction {
  id: number;
  reportId: number | null;
  adminId: string;
  action: ModActionKind;
  targetUserId: number | null;
  details: unknown;
  createdAt: number;
}

export interface RegionEvent {
  seq: number;
  ts: number;
  idx: number;
  li: number;
  lj: number;
  colour: number;
  prevColour: number;
  userId: number;
  source: 'user' | 'agent' | 'moderation';
}

export interface ClassifierVerdict {
  reportId: number;
  confidence: number;
  action: 'undo_region' | 'undo_user' | 'dismiss';
  label?: string;
}

export interface ApiKeyIssueRequest {
  owner: string;
  rateLimitPerMinute?: number;
}

export interface ApiKeyIssueResponse {
  id: number;
  owner: string;
  /** Shown once. */
  key: string;
  agentUserId: number;
}

export interface ParametersResponse {
  parameters: Partial<Record<ParameterKey, number>>;
}
